import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Icon, Checkbox, Accordion, Input } from "semantic-ui-react";
import { useMapInitialization } from "../hooks/useMapInit";
import { useAdhocSketchEvents } from "../hooks/selector/useAdhocSketchEvents";
import { useReadonlySketchEvents } from "../hooks/selector/useReadonlySketchEvents";
import { MapEditorToolbar } from "./admin/MapManagementEditToolbar";
import { useFeatureSelectorHistory } from "../hooks/selector/useFeatureSelectorHistory";
import { getHoverSymbolForGeometryType, getSymbolForGeometryType } from "../utils/arcgisHelper";
import { ADHOC_LAYER_ID, useAdhocFeatures } from "../hooks/selector/useAdhocFeatures";
import { useFeatureSearch } from "../hooks/selector/useFeatureSearch";
import classNames from "classnames";
import { compact } from "lodash";
import { useToasts } from "react-toast-notifications";
import { useClient } from "../../../hooks/useClient";
import { useParams } from "react-router-dom";
import "./FeatureSelectorModal.scss";
import { ToastStatuses } from "../../../components/global_components/Toast";

export const graphicAdhocIndex = new Map<string, number>();
export const graphicReadonlyIndex = new Map<string, number>();

export interface FeatureSelectorModalProps {
  onClose?: () => void;
  onSaveSelection: (selectedFeatures: number[]) => void;
  initialSelectedFeatures?: number[];
  hideSuccessToast?: boolean;
}

export function FeatureSelectorModal({
  onClose,
  onSaveSelection,
  initialSelectedFeatures = [],
  hideSuccessToast = false,
}: FeatureSelectorModalProps) {
  const { site } = useParams<{ site: string }>();
  const [expandedLayers, setExpandedLayers] = useState<{ [layerId: string]: boolean }>({ [ADHOC_LAYER_ID]: true });
  const [selectedFeatures, setSelectedFeatures] = useState<{ [key: string]: boolean }>({});
  const [hoveredFeatureId, setHoveredFeatureId] = useState<string | null>(null);
  const [adhocFeatureNames, setAdhocFeatureNames] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [loadingLayerFeatures, setLoadingLayerFeatures] = useState<{ [layerId: string]: boolean }>({});
  const [searchDiscoveredLayers, setSearchDiscoveredLayers] = useState<{
    [layerId: string]: { id: number; name: string; deactivated_at: string | nullish; color: string | null };
  }>({});
  const [initialSelectionLoaded, setInitialSelectionLoaded] = useState(false);
  const client = useClient(site);
  const cityDefaultCenter = client.query.data?.defaultCenter;
  const defaultCenter = useMemo(
    () => (cityDefaultCenter ? [cityDefaultCenter.lng, cityDefaultCenter.lat] : [-73.0193, 40.766]),
    [client.query.data],
  ) as [number, number];

  const { addToast } = useToasts();

  const {
    searchTerm,
    updateSearchTerm,
    isSearching,
    searchedLayers,
    searchedFeatures,
    searchFeaturesInLayer,
    searchFeaturesById,
  } = useFeatureSearch({ includeDeactivated: true }); // Always fetch deactivated records in case they are currently selected- filter them out later

  const {
    mapRef,
    sketchRef,
    sketchInitialized,
    readonlySketchRef,
    readonlySketchInitialized,
    editLayerRef,
    readonlyLayerRef,
    zoomRef,
  } = useMapInitialization({
    center: defaultCenter,
  });

  const {
    markFeatureAsChanged,
    markFeatureAsChangedDebounced,
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    clearHistory,
  } = useFeatureSelectorHistory({
    editLayerRef,
    sketchRef,
    readdHandlers: () => [],
    sketchCanUndo: false,
    sketchCanRedo: false,
  });

  const {
    selectedTool,
    handleToolSelect,
    getAdhocFeatures,
    sketchCanUndo,
    sketchCanRedo,
    featureTracking,
    setFeatureTracking,
  } = useAdhocSketchEvents({
    sketchRef,
    sketchInitialized,
    editLayerRef,
    markFeatureAsChanged,
    setSelectedFeatures,
    initialSelectionLoaded,
  });

  const { loadLayerFeatures, layerFeaturesMap, toggleFeatureSelection, unloadLayerFeatures } = useReadonlySketchEvents({
    readonlyLayerRef,
    readonlySketchRef,
    readonlySketchInitialized,
    selectedFeatures,
    setSelectedFeatures,
  });

  const {
    saveAdhocFeatures,
    isSaving: isAdhocSaving,
    refetchAdhocFeatures,
  } = useAdhocFeatures({
    editLayerRef,
    featureTracking,
    setFeatureTracking,
    selectedFeatures,
    initialSelectionLoaded,
    markFeatureAsChangedDebounced,
  });

  // Handle initial feature selection
  useEffect(() => {
    if (!searchFeaturesById || initialSelectionLoaded) return;
    if (initialSelectedFeatures.length === 0) return setInitialSelectionLoaded(true);

    const loadInitialSelection = async () => {
      try {
        setLoadingLayerFeatures((prev) => ({ ...prev, loading: true }));
        const featureIdsToSearch = initialSelectedFeatures.map((id) => id.toString());
        const allFeatures = await searchFeaturesById(featureIdsToSearch);

        if (!allFeatures || allFeatures.length === 0) {
          setInitialSelectionLoaded(true);
          setLoadingLayerFeatures((prev) => ({ ...prev, loading: false }));
          return;
        }

        // Group features by layer ID
        const featuresByLayer: Record<string, typeof allFeatures> = {};
        allFeatures.forEach((feature) => {
          const layerId =
            feature.feature_type === "Personal" && !feature.layer_id
              ? ADHOC_LAYER_ID
              : feature.layer_id
                ? feature.layer_id.toString()
                : "unknown";

          if (!featuresByLayer[layerId]) featuresByLayer[layerId] = [];
          featuresByLayer[layerId].push(feature);

          // If this is from a layer, store the layer info for UI
          if (layerId !== ADHOC_LAYER_ID && feature.layer && !searchDiscoveredLayers[layerId]) {
            setSearchDiscoveredLayers((prev) => ({
              ...prev,
              [layerId]: {
                id: parseInt(layerId),
                name: feature.layer?.name || `Layer ${layerId}`,
                deactivated_at: feature.layer?.deactivated_at,
                color: feature.layer?.color || null,
              },
            }));
          }
        });

        // Auto-expand layers with selected features
        const newExpandedLayers = { ...expandedLayers };

        // Process each layer's features
        await Promise.all(
          Object.entries(featuresByLayer).map(async ([layerId, features]) => {
            newExpandedLayers[layerId] = true;
            setLoadingLayerFeatures((prev) => ({ ...prev, [layerId]: true }));

            if (layerId !== ADHOC_LAYER_ID) await loadLayerFeatures(layerId);

            // Mark all features as selected
            features.forEach((feature) => {
              if (layerId !== ADHOC_LAYER_ID) toggleFeatureSelection(feature.id.toString(), true);
              else
                setSelectedFeatures((prev) => ({
                  ...prev,
                  [`adhoc-${feature.id}`]: true,
                }));
            });

            setLoadingLayerFeatures((prev) => ({ ...prev, [layerId]: false }));
          }),
        );
        setExpandedLayers(newExpandedLayers);
        clearHistory();
        setInitialSelectionLoaded(true);
      } catch (error) {
        console.error("Error loading initial selection:", error);
        addToast("Failed to load initial selection", {
          appearance: ToastStatuses.Error,
          autoDismiss: true,
        });
      } finally {
        setLoadingLayerFeatures((prev) => ({ ...prev, loading: false }));
        refetchAdhocFeatures();
      }
    };

    loadInitialSelection();
  }, [initialSelectedFeatures, initialSelectionLoaded, searchFeaturesById]);

  useEffect(() => {
    if (!searchTerm || !searchedLayers) return;
    const discoveredLayers: typeof searchDiscoveredLayers = {};

    // Extract layer information from feature search results
    Object.entries(searchedFeatures).forEach(([layerId, features]) => {
      features.forEach((feature) => {
        if (!feature.layer) return;
        discoveredLayers[layerId] = {
          id: parseInt(layerId),
          name: feature.layer.name,
          deactivated_at: feature.layer.deactivated_at,
          color: feature.layer.color,
        };
      });
    });

    setSearchDiscoveredLayers(discoveredLayers);
  }, [searchedFeatures]);

  // Update graphics indexes when features change
  useEffect(() => {
    // Index adhoc features
    if (editLayerRef.current) {
      graphicAdhocIndex.clear();
      const adhocGraphics = editLayerRef.current.graphics.toArray();
      adhocGraphics.forEach((graphic, index) => {
        if (graphic.attributes?.graphicId) {
          graphicAdhocIndex.set(graphic.attributes.graphicId, index);
        }
      });
    }

    // Index readonly features
    if (readonlyLayerRef.current) {
      graphicReadonlyIndex.clear();
      const readonlyGraphics = readonlyLayerRef.current.graphics.toArray();
      readonlyGraphics.forEach((graphic, index) => {
        if (graphic.attributes?.featureId) {
          graphicReadonlyIndex.set(graphic.attributes.featureId.toString(), index);
        }
      });
    }
  }, [editLayerRef.current?.graphics, readonlyLayerRef.current?.graphics, featureTracking]);

  // Initialize adhoc feature names when they are created or loaded
  useEffect(() => {
    if (!editLayerRef.current) return;
    const adhocFeatures = getAdhocFeatures();
    const newNames: { [key: string]: string } = {};

    adhocFeatures.forEach((feature) => {
      const graphicId = feature.attributes?.graphicId;
      if (graphicId && !adhocFeatureNames[graphicId]) {
        newNames[graphicId] = feature.attributes?.name || `My Feature ${feature.attributes?.index + 1}`;
      }
    });

    if (Object.keys(newNames).length > 0) {
      setAdhocFeatureNames((prev) => ({ ...prev, ...newNames }));
    }
  }, [editLayerRef.current?.graphics.length, getAdhocFeatures]);

  const toggleLayerExpansion = async (layerId: string) => {
    if (layerId === ADHOC_LAYER_ID) return;
    setExpandedLayers((prev) => {
      const newState = { ...prev, [layerId]: !prev[layerId] };

      // If expanding, load features for this layer
      if (newState[layerId] && layerId !== ADHOC_LAYER_ID) {
        setLoadingLayerFeatures((prev) => ({ ...prev, [layerId]: true }));
        if (searchTerm) {
          searchFeaturesInLayer(parseInt(layerId), searchTerm)
            .then((features) => {
              // Convert to format expected by readonly layer
              const formattedFeatures = features.map((feature) => ({
                id: feature.id,
                name: feature.primary_field_value || `Feature ${feature.id}`,
                geometry: feature.geometry,
                attributes: {},
              }));

              // Add to readonly layer
              loadLayerFeatures(layerId, formattedFeatures);
            })
            .finally(() => {
              setLoadingLayerFeatures((prev) => ({ ...prev, [layerId]: false }));
            });
        } else {
          loadLayerFeatures(layerId).finally(() => {
            setLoadingLayerFeatures((prev) => ({ ...prev, [layerId]: false }));
          });
        }
      } else {
        // If collapsing, unload features and deselect them
        if (layerId !== ADHOC_LAYER_ID) unloadLayerFeatures(layerId);

        // Deselect any selected features from this layer
        setSelectedFeatures((prevSelected) => {
          const newSelected = { ...prevSelected };
          if (layerId === ADHOC_LAYER_ID) {
            getAdhocFeatures().forEach((feature) => {
              if (feature.attributes?.graphicId && newSelected[feature.attributes.graphicId]) {
                delete newSelected[feature.attributes.graphicId];
              }
            });
          } else {
            (layerFeaturesMap[layerId] || []).forEach((feature) => {
              if (newSelected[feature.id.toString()]) {
                delete newSelected[feature.id.toString()];
              }
            });
          }
          return newSelected;
        });
      }

      return newState;
    });
  };

  // Handle feature hover
  const handleFeatureHover = (featureId: string | null, layerId?: string) => {
    if (hoveredFeatureId === featureId) return;

    // Reset previous hovered feature
    if (hoveredFeatureId && !selectedFeatures[hoveredFeatureId]) {
      if (readonlyLayerRef.current) {
        if (graphicReadonlyIndex.has(hoveredFeatureId)) {
          const prevGraphic = readonlyLayerRef.current.graphics.at(graphicReadonlyIndex.get(hoveredFeatureId)!);
          if (prevGraphic?.geometry) {
            prevGraphic.symbol = getSymbolForGeometryType(prevGraphic.geometry.type);
          }
        }
      }

      if (editLayerRef.current) {
        if (graphicAdhocIndex.has(hoveredFeatureId)) {
          const prevAdhocFeature = editLayerRef.current.graphics.at(graphicAdhocIndex.get(hoveredFeatureId)!);
          if (prevAdhocFeature?.geometry) {
            prevAdhocFeature.symbol = getSymbolForGeometryType(prevAdhocFeature.geometry.type);
          }
        }
      }
    }

    // Apply hover style to new hovered feature
    if (featureId) {
      if (layerId === ADHOC_LAYER_ID) {
        if (editLayerRef.current && graphicAdhocIndex.has(featureId)) {
          const feature = editLayerRef.current.graphics.at(graphicAdhocIndex.get(featureId)!);
          if (feature?.geometry) {
            feature.symbol = getHoverSymbolForGeometryType(feature.geometry.type);
          }
        }
      } else {
        if (readonlyLayerRef.current && graphicReadonlyIndex.has(featureId)) {
          const feature = readonlyLayerRef.current.graphics.at(graphicReadonlyIndex.get(featureId)!);
          if (feature?.geometry) {
            feature.symbol = getHoverSymbolForGeometryType(feature.geometry.type);
          }
        }
      }
    }

    setHoveredFeatureId(featureId);
  };

  const updateAdhocFeatureName = (graphicId: string, newName: string) => {
    setAdhocFeatureNames((prev) => ({ ...prev, [graphicId]: newName }));

    // Update the graphic attribute
    if (!editLayerRef.current || !graphicAdhocIndex.has(graphicId)) return;
    const feature = editLayerRef.current.graphics.at(graphicAdhocIndex.get(graphicId)!);
    if (!feature) return;
    feature.attributes.name = newName;
    markFeatureAsChanged(graphicId);

    // Mark as modified if it has a featureId (already exists on server)
    if (!feature.attributes.featureId) return;
    setFeatureTracking((prev) => ({
      ...prev,
      [graphicId]: {
        ...prev[graphicId],
        featureId: feature.attributes.featureId,
        isModified: true,
      },
    }));
  };

  const deleteAdhocFeature = (graphicId: string) => {
    if (!editLayerRef.current || !graphicAdhocIndex.has(graphicId)) return;
    const feature = editLayerRef.current.graphics.at(graphicAdhocIndex.get(graphicId)!);
    if (!feature) return;
    const featureId = feature.attributes.featureId;

    // Check if this is a persisted feature
    if (featureId) {
      setFeatureTracking((prev) => ({
        ...prev,
        [graphicId]: {
          ...prev[graphicId],
          featureId,
          isDeleted: true,
        },
      }));
      feature.visible = false;
    } else {
      editLayerRef.current.graphics.remove(feature);
    }

    setSelectedFeatures((prev) => {
      const newState = { ...prev };
      delete newState[graphicId];
      return newState;
    });
    setAdhocFeatureNames((prev) => {
      const newState = { ...prev };
      delete newState[graphicId];
      return newState;
    });
    markFeatureAsChanged(graphicId);
    graphicAdhocIndex.delete(graphicId);
  };

  const getFilteredLayers = () => {
    const filteredLayers = [
      ...(searchedLayers.filter((layer) => {
        // If layer was previously selected, keep it visible even if it's been deactivated
        if (expandedLayers[layer.id] !== undefined) {
          return true;
        }

        return !layer.deactivated_at;
      }) || []),
    ];

    // Add layers discovered from feature search results
    if (!searchTerm) return filteredLayers;
    const existingLayerIds = new Set(filteredLayers.map((layer) => layer.id));
    Object.values(searchDiscoveredLayers).forEach(({ id, name, deactivated_at, color }) => {
      if (existingLayerIds.has(id)) return;
      filteredLayers.push({
        id,
        name,
        has_polygonal_features: true,
        elasticsearch_updated_at: new Date().toString(),
        city_id: 0,
        created_at: new Date().toString(),
        updated_at: new Date().toString(),
        description: null,
        field_definitions: [],
        deactivated_at: deactivated_at ?? null,
        color,
      });
    });
    return filteredLayers;
  };

  const getFilteredAdhocFeatures = () => {
    if (!searchTerm.trim() || !editLayerRef.current) {
      return getAdhocFeatures().filter((feature) => {
        const graphicId = feature.attributes?.graphicId;
        return !featureTracking[graphicId]?.isDeleted;
      });
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    const adhocFeatures = getAdhocFeatures();
    return adhocFeatures.filter((feature) => {
      const graphicId = feature.attributes?.graphicId;
      if (featureTracking[graphicId]?.isDeleted) return false;
      const featureName = adhocFeatureNames[graphicId] || feature.attributes?.name || "";
      return featureName.toLowerCase().includes(lowerSearchTerm);
    });
  };

  const getFilteredLayerFeatures = (layerId: string) => {
    // If search is active and we have search results for this layer, use those
    if (searchTerm.trim() && searchedFeatures[layerId]) {
      return searchedFeatures[layerId].map((feature) => ({
        id: feature.id,
        name: feature.primary_field_value || `Feature ${feature.id}`,
        geometry: feature.geometry,
        attributes:
          feature.custom_field_values?.reduce(
            (acc, field) => {
              // We're only using text actually, but just for future proofing
              const value = field.value_text || field.value_number || field.value_boolean || field.value_json;
              if (value !== null && value !== undefined) acc[field.custom_field_id] = value;
              return acc;
            },
            {} as Record<string, any>,
          ) || {},
      }));
    }

    // Otherwise use the features loaded in the layer
    return layerFeaturesMap[layerId] || [];
  };

  // Combine all selected features (adhoc + predefined)
  const getAllSelectedFeatures = async () => {
    const createdMap = await saveAdhocFeatures(); // Save any adhoc features that need to be persisted

    const adhocFeatures = compact(
      Object.keys(selectedFeatures)
        .filter((graphicId) => selectedFeatures[graphicId] && graphicId.startsWith("adhoc-"))
        .map((graphicId) => {
          if (createdMap[graphicId]) return createdMap[graphicId];
          if (!editLayerRef.current || !graphicAdhocIndex.has(graphicId)) return null;
          const graphic = editLayerRef.current.graphics.at(graphicAdhocIndex.get(graphicId)!);
          if (!graphic || !graphic.attributes.featureId) return null;
          return +graphic.attributes.featureId;
        }),
    );

    const predefinedFeatures = compact(
      Object.keys(selectedFeatures)
        .filter((featureId) => selectedFeatures[featureId] && !featureId.startsWith("adhoc-"))
        .map((featureId) => {
          // Check in layer features map first
          for (const [, features] of Object.entries(layerFeaturesMap)) {
            const feature = features.find((f) => f.id.toString() === featureId);
            if (!feature) continue;
            return feature.id;
          }

          // Check in search results if not found
          for (const [, features] of Object.entries(searchedFeatures)) {
            const feature = features.find((f) => f.id.toString() === featureId);
            if (feature) return feature.id;
          }

          return null;
        }),
    );

    return [...adhocFeatures, ...predefinedFeatures];
  };

  // Handle save button click
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const selectedFeaturesList = compact(await getAllSelectedFeatures());
      if (selectedFeaturesList.length === 0) {
        addToast("You must select at least one feature", {
          appearance: ToastStatuses.Error,
          autoDismiss: true,
        });
        setIsSaving(false);
        return;
      }
      onSaveSelection(selectedFeaturesList);
      if (!hideSuccessToast)
        addToast("Selection saved!", {
          appearance: ToastStatuses.Success,
          autoDismiss: true,
        });
      onClose?.();
    } catch (error) {
      console.error("Error saving selection:", error);
      addToast("Error saving selection. Please try again.", {
        appearance: ToastStatuses.Error,
        autoDismiss: true,
      });
      setIsSaving(false);
    }
  };

  const filteredLayers = getFilteredLayers();
  const filteredAdhocFeatures = getFilteredAdhocFeatures();

  return (
    <Modal size='fullscreen' open={true} onClose={onClose} className='feature-selector-modal'>
      <Modal.Header>
        <div className='tw-flex tw-items-center tw-justify-between'>
          <span>Select Boundaries</span>
          <div className='tw-flex tw-items-center'>
            <Button className='tw-bg-blue tw-text-white' onClick={handleSave} disabled={isSaving || isAdhocSaving}>
              {isSaving || isAdhocSaving ? "Saving..." : "Save"}
            </Button>
            <Button basic onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Content className='tw-p-0'>
        <div className='tw-flex tw-h-full tw-w-full tw-flex-col'>
          <div className='mapping-map tw-relative tw-h-[calc(100vh-180px)] tw-w-full tw-overflow-x-hidden'>
            {/* Map */}
            <div className='tw-bg-gray-100 tw-h-full tw-w-full tw-overflow-hidden tw-rounded-md' ref={mapRef}></div>

            {/* Toolbar */}
            <MapEditorToolbar
              sketchRef={sketchRef}
              zoomRef={zoomRef}
              handleToolSelect={handleToolSelect}
              selectedTool={selectedTool}
              handleUndo={handleUndo}
              handleRedo={handleRedo}
              canUndo={canUndo || sketchCanUndo}
              canRedo={canRedo || sketchCanRedo}
            />

            {/* Layers and Features Sidebar */}
            <div className='tw-absolute tw-right-2 tw-top-12 tw-z-20 tw-flex tw-h-[50%] tw-w-80 tw-transform tw-flex-col tw-rounded-md tw-bg-white tw-p-4 tw-shadow-lg sm:tw-top-2 sm:tw-h-[70%]'>
              {/* Search box */}
              <Input
                iconPosition='left'
                fluid
                placeholder='Search layers and features...'
                className='tw-mb-4 tw-rounded'
                value={searchTerm}
                onChange={(e) => updateSearchTerm(e.target.value)}
                loading={isSearching}
              >
                <Icon name='search' />
                <input className='tw-rounded-full' />
              </Input>
              {/* Layer List */}
              <div className='tw-flex-1 tw-overflow-y-auto'>
                {isSearching && !filteredLayers.length ? (
                  <p className='tw-py-4 tw-text-center'>Searching...</p>
                ) : filteredLayers.length || filteredAdhocFeatures.length ? (
                  <div className='tw-flex-grow'>
                    {/* Predefined layers */}
                    {filteredLayers.map((layer) => (
                      <Accordion
                        key={layer.id}
                        className={classNames("tw-mb-2 tw-border-none", {
                          "tw-opacity-50": !!layer.deactivated_at,
                        })}
                      >
                        <Accordion.Title
                          active={expandedLayers[layer.id]}
                          onClick={() => toggleLayerExpansion(layer.id.toString())}
                          className='layer-hover tw-flex tw-items-center tw-rounded-md tw-py-2'
                          style={{
                            "--layer-color-bg": `${layer.color || "#1678C2"}15`,
                          }}
                        >
                          <Icon
                            name={expandedLayers[layer.id] ? "circle" : "circle outline"}
                            style={{ color: layer.color || "#1678C2" }}
                            size='large'
                          />
                          <span className='tw-font-semibold'>{layer.name}</span>
                          {searchTerm && searchedFeatures[layer.id] && (
                            <span className='tw-text-gray-500 tw-ml-2 tw-text-sm'>
                              ({searchedFeatures[layer.id].length})
                            </span>
                          )}
                        </Accordion.Title>
                        <Accordion.Content active={expandedLayers[layer.id]}>
                          <div className='tw-pl-4'>
                            {/* Select all / Deselect All text buttons */}
                            {!layer.deactivated_at && (
                              <div className='tw-mb-2 tw-flex'>
                                <Button
                                  size='mini'
                                  className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0 tw-text-blue tw-shadow-none'
                                  onClick={() => {
                                    const filteredFeatures = getFilteredLayerFeatures(layer.id.toString());
                                    filteredFeatures.forEach((feature) => {
                                      setSelectedFeatures((prev) => ({
                                        ...prev,
                                        [feature.id.toString()]: true,
                                      }));
                                      toggleFeatureSelection(feature.id.toString(), true);
                                    });
                                  }}
                                >
                                  Select All
                                </Button>
                                <Button
                                  size='mini'
                                  className='tw-m-0 tw-ml-2 tw-border-none tw-bg-transparent tw-p-0 tw-text-blue tw-shadow-none'
                                  onClick={() => {
                                    const filteredFeatures = getFilteredLayerFeatures(layer.id.toString());
                                    filteredFeatures.forEach((feature) => {
                                      setSelectedFeatures((prev) => ({
                                        ...prev,
                                        [feature.id.toString()]: false,
                                      }));
                                      toggleFeatureSelection(feature.id.toString(), false);
                                    });
                                  }}
                                >
                                  Deselect All
                                </Button>
                              </div>
                            )}

                            {/* Scrollable feature list */}
                            <div className='tw-mr-2 tw-overflow-y-auto'>
                              {expandedLayers[layer.id] && loadingLayerFeatures[layer.id] ? (
                                <p className='tw-text-gray-500 tw-text-sm tw-italic'>Loading features...</p>
                              ) : expandedLayers[layer.id] && getFilteredLayerFeatures(layer.id.toString()).length ? (
                                getFilteredLayerFeatures(layer.id.toString()).map((feature) => (
                                  <div
                                    key={feature.id}
                                    className='tw-mb-1 tw-flex tw-items-center'
                                    onMouseEnter={() => handleFeatureHover(feature.id.toString(), layer.id.toString())}
                                    onMouseLeave={() => handleFeatureHover(null)}
                                  >
                                    <Checkbox
                                      checked={selectedFeatures[feature.id.toString()] || false}
                                      onChange={(event, data) =>
                                        toggleFeatureSelection(feature.id.toString(), data.checked)
                                      }
                                      label={feature.name || `Feature ${feature.id}`}
                                      className='tw-flex-1'
                                    />
                                  </div>
                                ))
                              ) : expandedLayers[layer.id] ? (
                                searchTerm.trim() ? (
                                  <p className='tw-text-gray-500 tw-text-sm tw-italic'>No matching features found</p>
                                ) : (
                                  <p className='tw-text-gray-500 tw-text-sm tw-italic'>No features available</p>
                                )
                              ) : (
                                <p className='tw-text-gray-500 tw-text-sm tw-italic'>Click to load features</p>
                              )}
                            </div>
                          </div>
                        </Accordion.Content>
                      </Accordion>
                    ))}

                    {/* Ad hoc features */}
                    <Accordion className='tw-mb-2 tw-border-none'>
                      <Accordion.Title
                        active={expandedLayers[ADHOC_LAYER_ID]}
                        onClick={() => toggleLayerExpansion(ADHOC_LAYER_ID)}
                        className='tw-flex tw-items-center tw-rounded-md tw-bg-transparent tw-py-2 tw-transition-colors hover:tw-bg-orange hover:tw-bg-opacity-10'
                      >
                        <Icon
                          name={expandedLayers[ADHOC_LAYER_ID] ? "circle" : "circle outline"}
                          className='tw-text-orange'
                          size='large'
                        />
                        <span className='tw-font-semibold'>My Features</span>
                        {searchTerm && filteredAdhocFeatures.length > 0 && (
                          <span className='tw-text-gray-500 tw-ml-2 tw-text-sm'>({filteredAdhocFeatures.length})</span>
                        )}
                      </Accordion.Title>
                      <Accordion.Content active={expandedLayers[ADHOC_LAYER_ID]}>
                        <div className='tw-pl-4'>
                          {/* Select all / Deselect All text buttons for adhoc features */}
                          <div className='tw-mb-2 tw-flex'>
                            <Button
                              size='mini'
                              className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0 tw-text-blue tw-shadow-none'
                              onClick={() => {
                                filteredAdhocFeatures.forEach((feature) => {
                                  setSelectedFeatures((prev) => ({
                                    ...prev,
                                    [feature.attributes?.graphicId]: true,
                                  }));
                                  handleFeatureHover(feature.attributes?.graphicId, ADHOC_LAYER_ID);
                                });
                              }}
                            >
                              Select All
                            </Button>
                            <Button
                              size='mini'
                              className='tw-m-0 tw-ml-2 tw-border-none tw-bg-transparent tw-p-0 tw-text-blue tw-shadow-none'
                              onClick={() => {
                                filteredAdhocFeatures.forEach((feature) => {
                                  setSelectedFeatures((prev) => ({
                                    ...prev,
                                    [feature.attributes?.graphicId]: false,
                                  }));
                                  handleFeatureHover(feature.attributes?.graphicId, ADHOC_LAYER_ID);
                                });
                              }}
                            >
                              Deselect All
                            </Button>
                          </div>

                          {/* Scrollable adhoc feature list */}
                          <div className='tw-overflow-y-auto tw-pr-1'>
                            {filteredAdhocFeatures.length ? (
                              filteredAdhocFeatures
                                .sort((a, b) => {
                                  const aGraphicId = a.attributes?.graphicId || "";
                                  const bGraphicId = b.attributes?.graphicId || "";
                                  return aGraphicId.localeCompare(bGraphicId);
                                })
                                .map((feature) => {
                                  const graphicId = feature.attributes?.graphicId;
                                  const featureName =
                                    adhocFeatureNames[graphicId] ||
                                    feature.attributes?.name ||
                                    `Ad hoc Feature ${feature.attributes?.index + 1}`;

                                  return (
                                    <div
                                      key={graphicId}
                                      className='tw-mb-1 tw-flex tw-items-center'
                                      onMouseEnter={() => handleFeatureHover(graphicId, ADHOC_LAYER_ID)}
                                      onMouseLeave={() => handleFeatureHover(null)}
                                    >
                                      <Checkbox
                                        checked={selectedFeatures[graphicId] || false}
                                        onChange={() => {
                                          setSelectedFeatures((prev) => ({
                                            ...prev,
                                            [graphicId]: !prev[graphicId],
                                          }));
                                        }}
                                        className='tw-mr-2'
                                      />
                                      <Input
                                        value={featureName}
                                        onChange={(e) => updateAdhocFeatureName(graphicId, e.target.value)}
                                        size='small'
                                        className='tw-flex-1'
                                        onClick={(e: any) => e.stopPropagation()}
                                      />
                                      <Icon
                                        name='trash alternate'
                                        className='tw-ml-2 tw-cursor-pointer tw-text-red'
                                        onClick={() => deleteAdhocFeature(graphicId)}
                                      />
                                    </div>
                                  );
                                })
                            ) : (
                              <p className='tw-text-gray-500 tw-text-sm tw-italic'>
                                {searchTerm.trim() ? "No matching features found" : "No ad hoc features created yet"}
                              </p>
                            )}
                          </div>
                        </div>
                      </Accordion.Content>
                    </Accordion>
                  </div>
                ) : (
                  <p className='tw-text-gray-500 tw-text-sm tw-italic'>
                    {isSearching ? (
                      "Searching for layers..."
                    ) : searchTerm.trim() ? (
                      "No matching layers or features found."
                    ) : (
                      <p className='tw-text-center'>
                        No boundaries found.
                        <br />
                        Draw a boundary or create a new layer&nbsp;
                        <a href={`${window.location.protocol}//${window.location.host}/${site}/admin/mapping`}>here</a>.
                      </p>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}
export default FeatureSelectorModal;
